import type { useQueryClient } from '@tanstack/react-query';
import type { IEnvironment } from 'relay-runtime';

import type { logException } from '@atlassian/atlassian-studio-observability';

import type {
	readLastWorkspace,
	writeLastWorkspace,
} from '../../common/utils/last-workspace-storage';
import type { trackLastWorkspaceWrite } from '../../common/utils/last-workspace-storage/analytics';
import {
	type fetchAvailableUnitAwareWorkspaces,
	getAvailableUnitAwareWorkspacesQueryKey,
	type getRecentWorkspaceForUser,
	getRecentWorkspaceForUserQueryKey,
} from '../../services';

import type { WorkspaceContext } from './index';

interface RootWorkspaceDependencies {
	atlassianAccountId: string;
	environment: IEnvironment;
	fetchAvailableUnitAwareWorkspaces: typeof fetchAvailableUnitAwareWorkspaces;
	getRecentWorkspaceForUser: typeof getRecentWorkspaceForUser;
	logException: typeof logException;
	queryClient: ReturnType<typeof useQueryClient>;
	readLastWorkspace: typeof readLastWorkspace;
	trackLastWorkspaceWrite: typeof trackLastWorkspaceWrite;
	writeLastWorkspace: typeof writeLastWorkspace;
}

export const resolveRootWorkspaceContext = async ({
	queryClient,
	environment,
	atlassianAccountId,
	readLastWorkspace,
	writeLastWorkspace,
	trackLastWorkspaceWrite,
	fetchAvailableUnitAwareWorkspaces,
	getRecentWorkspaceForUser,
	logException,
}: RootWorkspaceDependencies): Promise<WorkspaceContext> => {
	const response = await queryClient.fetchQuery({
		queryKey: [...getAvailableUnitAwareWorkspacesQueryKey(), atlassianAccountId],
		queryFn: fetchAvailableUnitAwareWorkspaces,
		staleTime: Infinity,
	});
	const workspaces = [...new Set(Object.values(response).filter(Boolean))];
	if (workspaces.length <= 1) {
		return {
			workspaces,
			isReady: true,
			resolutionSource: workspaces.length ? 'singleWorkspace' : 'noWorkspace',
			workspaceCount: workspaces.length,
		};
	}

	const storedWorkspace = readLastWorkspace(atlassianAccountId);
	if (storedWorkspace && workspaces.includes(storedWorkspace.workspaceId)) {
		return {
			workspaces: [storedWorkspace.workspaceId],
			isReady: true,
			resolutionSource: 'localStorage',
			workspaceCount: workspaces.length,
		};
	}

	// Remove this fallback in follow-up work as part of the Worklens migration.
	try {
		const recentWorkspace = await queryClient.fetchQuery({
			queryKey: getRecentWorkspaceForUserQueryKey(atlassianAccountId),
			queryFn: async () =>
				(await getRecentWorkspaceForUser(environment, queryClient, workspaces)) ?? null,
			staleTime: Infinity,
		});
		if (recentWorkspace && workspaces.includes(recentWorkspace)) {
			trackLastWorkspaceWrite(
				writeLastWorkspace(atlassianAccountId, recentWorkspace),
				'noWorkspaceInUrl',
			);
			return {
				workspaces: [recentWorkspace],
				isReady: true,
				resolutionSource: 'worklens',
				workspaceCount: workspaces.length,
			};
		}
	} catch (error) {
		logException(error, { loggerName: 'studio-workspace-context', teamName: 'studio' });
	}

	return {
		workspaces,
		isReady: true,
		resolutionSource: 'picker',
		workspaceCount: workspaces.length,
	};
};
